/* Independent native search sections; failed requests keep usable page links. */
(() => {
  if (customElements.get('mm-search-results')) return;
  const groups = {
    products: { section: 'mm-search-products', type: 'product', label: 'products' },
    content: { section: 'mm-search-content', type: 'page,article', label: 'pages and articles' }
  };
  const integer = value => /^\d+$/.test(value || '') && Number.isSafeInteger(Number(value)) ? Number(value) : null;
  const sameTypes = (left, right) => left.split(',').sort().join(',') === right.split(',').sort().join(',');

  customElements.define('mm-search-results', class extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.generation = (this.generation || 0) + 1;
      this.requests = new Map();
      const mode = this.dataset.searchMode || 'all';
      this.groups = [...this.querySelectorAll('[data-mm-search-group]')].filter(group =>
        groups[group.dataset.mmSearchGroup] && (mode === 'all' || mode === group.dataset.mmSearchGroup));
      this.onClick = event => {
        const link = event.target.closest('a[data-mm-search-more]');
        if (!link || !this.contains(link) || event.defaultPrevented || event.button !== 0 ||
          event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download') ||
          (link.target && link.target !== '_self')) return;
        const group = link.closest('[data-mm-search-group]');
        if (!this.groups.includes(group)) return;
        if (this.requests.has(group)) { event.preventDefault(); return; }
        const url = this.requestURL(group, link);
        if (!url) return;
        event.preventDefault();
        this.load(group, url, true);
      };
      this.addEventListener('click', this.onClick);
      this.updateTotal();
      this.groups.forEach(group => {
        if (group.dataset.mmSearchComplete === 'true') return;
        const url = this.requestURL(group);
        if (url) this.load(group, url, false);
      });
    }

    requestURL(group, link) {
      const config = groups[group.dataset.mmSearchGroup];
      if (!config || group.dataset.mmSearchSection !== config.section) return null;
      try {
        const url = new URL(this.dataset.searchUrl, window.location.href);
        if (url.origin !== window.location.origin) return null;
        url.search = window.location.search;
        ['page', 'sections', 'section_id', 'view'].forEach(key => url.searchParams.delete(key));
        let page = 1, type = config.type;
        if (link) {
          const next = new URL(link.href, window.location.href);
          const currentType = new URL(window.location.href).searchParams.get('type');
          const expectedType = group.dataset.mmSearchComplete === 'true' && group.dataset.mmSearchGroup === 'content' &&
            ['page', 'article'].includes(currentType) ? currentType : config.type;
          page = integer(next.searchParams.get('page'));
          type = next.searchParams.get('type') || '';
          if (next.origin !== url.origin || next.pathname !== url.pathname ||
            !sameTypes(type, expectedType) || page !== integer(group.dataset.mmSearchPage) + 1) return null;
        }
        url.hash = '';
        url.searchParams.set('q', this.dataset.searchQuery || '');
        url.searchParams.set('type', type);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', config.section);
        return url;
      } catch (_) { return null; }
    }

    setBusy(group, busy) {
      group.setAttribute('aria-busy', String(busy));
      group.querySelectorAll('[data-mm-search-more]').forEach(link => {
        if (busy) link.setAttribute('aria-disabled', 'true');
        else link.removeAttribute('aria-disabled');
      });
    }

    status(group, text) {
      const status = group.querySelector('[data-mm-search-status]');
      if (status) status.textContent = text;
    }

    current(group, task) {
      return this.connected && this.generation === task.generation && this.requests.get(group) === task;
    }

    finish(group, task) {
      clearTimeout(task.timeout);
      if (this.requests.get(group) === task) {
        this.requests.delete(group);
        this.setBusy(group, false);
      }
    }

    async load(group, url, append) {
      if (this.requests.has(group)) return;
      const task = { controller: new AbortController(), generation: this.generation };
      this.requests.set(group, task);
      this.setBusy(group, true);
      this.status(group, append ? 'Loading more results…' : 'Loading results…');
      const failure = 'Results could not be loaded. Use the result links or try again.';
      task.timeout = setTimeout(() => {
        if (!this.current(group, task)) return;
        task.controller.abort();
        this.status(group, failure);
        this.finish(group, task);
      }, 12000);
      try {
        const response = await fetch(url.href, {
          credentials: 'same-origin', signal: task.controller.signal, headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('Search request failed');
        const markup = await response.text();
        if (!this.current(group, task)) return;
        const document = new DOMParser().parseFromString(markup, 'text/html');
        const key = group.dataset.mmSearchGroup, config = groups[key];
        const matches = [...document.querySelectorAll('[data-mm-search-group]')]
          .filter(candidate => candidate.dataset.mmSearchGroup === key);
        const next = matches[0];
        const count = next && integer(next.dataset.mmSearchCount);
        const page = next && integer(next.dataset.mmSearchPage);
        if (matches.length !== 1 || next.dataset.mmSearchSection !== config.section ||
          next.dataset.mmSearchComplete !== 'true' || count === null ||
          page !== Number(url.searchParams.get('page')) || !next.querySelector('[data-mm-search-items]') ||
          !next.querySelector('[data-mm-search-controls]') || !next.querySelector('[data-mm-search-status]')) {
          throw new Error('Search section is incomplete');
        }
        next.querySelectorAll('script').forEach(script => script.remove());
        const items = next.querySelector('[data-mm-search-items]');
        const added = [...items.children];
        const currentItems = group.querySelector('[data-mm-search-items]');
        if (added.length > count || (append && (!currentItems || currentItems.children.length + added.length > count))) {
          throw new Error('Search result count does not match its cards');
        }
        if (append) {
          currentItems.append(...added);
          ['controls', 'progress', 'count-label', 'empty'].forEach(name => {
            const old = group.querySelector(`[data-mm-search-${name}]`);
            const replacement = next.querySelector(`[data-mm-search-${name}]`);
            if (old && replacement) old.replaceWith(replacement);
          });
        } else {
          group.replaceChildren(...next.childNodes);
        }
        ['mmSearchComplete', 'mmSearchCount', 'mmSearchPage'].forEach(name => { group.dataset[name] = next.dataset[name]; });
        const countLabel = group.querySelector('[data-mm-search-count-label]');
        if (countLabel) countLabel.hidden = false;
        this.updateTotal();
        this.status(group, append ? `${added.length} more ${config.label} loaded.` : `${count} ${config.label} found.`);
        if (append && added.length) {
          const first = added[0].matches('a[href]') ? added[0] : added[0].querySelector('a[href]');
          first?.focus();
        }
      } catch (_) {
        if (this.current(group, task)) this.status(group, failure);
      } finally {
        if (this.current(group, task)) this.finish(group, task);
      }
    }

    updateTotal() {
      const total = this.querySelector('[data-mm-search-total]');
      if (!total) return;
      const counts = this.groups.map(group => group.dataset.mmSearchComplete === 'true' ? integer(group.dataset.mmSearchCount) : null);
      if (!counts.length || counts.some(count => count === null)) { total.hidden = true; return; }
      const count = counts.reduce((sum, value) => sum + value, 0);
      let formatted = String(count);
      try { formatted = new Intl.NumberFormat(document.documentElement.lang || undefined).format(count); } catch (_) { /* Keep numeric fallback. */ }
      total.textContent = `${formatted} ${count === 1 ? 'result' : 'results'}`;
      total.hidden = false;
    }

    disconnectedCallback() {
      this.connected = false;
      this.removeEventListener('click', this.onClick);
      this.requests?.forEach((task, group) => {
        task.controller.abort();
        this.finish(group, task);
      });
    }
  });
})();
